import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { useFetcherPublic } from '@app/hooks/fetcher';
import { getSetting, useSetting } from '@app/hooks/user/mysetting';
import { SETTING_TYPE_SYSTEM } from '@app/constants/SETTING';
import CSelect from '@app/components/Common/CSelect';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: setting, updateSetting } = useSetting(SETTING_TYPE_SYSTEM);
  const { data: currencyResult } = useFetcherPublic('/system/currency');

  const currentCurrency = getSetting(setting, 'CURRENCY', 'USD');
  const currencyOptions = currencyResult?.result?.result || [];

  const defaultCurrency = currencyOptions?.find(
    (item: any) => item.code === currentCurrency
  );

  const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency);

  useEffect(() => {
    if (defaultCurrency) {
      setSelectedCurrency(defaultCurrency);
    }
  }, [currentCurrency, defaultCurrency]);

  const languageSelectOptions = [
    { value: 'en', label: 'English' },
    { value: 'ko', label: '한국어' },
  ];

  const currencySelectOptions =
    currencyOptions.length > 0
      ? currencyOptions.map((currency: any) => ({
          value: currency.code,
          label: currency.name,
        }))
      : [{ value: 'USD', label: '$ USD' }];

  const handleLanguageChange = (value: string | number) => {
    i18n.changeLanguage(value as string);
  };

  const handleCurrencyChange = (value: string | number) => {
    const selected = currencyOptions.find((item: any) => item.code === value);
    setSelectedCurrency(selected);
    updateSetting('CURRENCY', value as string);
  };

  // 페이지 이동 시 스크롤 최상단으로 이동
  const handleNavigate = (route: string) => {
    navigate(route);
    window.scrollTo(0, 0);
  };

  const footerLinks = [
    {
      label: t('Crypto'),
      items: [{ name: t('Buy/Sell Crypto'), route: '/buysell-crypto' }],
    },
    {
      label: t('Markets'),
      items: [
        { name: t('Markets Overview'), route: '/markets' },
        { name: t('Fear & Greed Index'), route: '/fear-greed-index' },
      ],
    },
    {
      label: t('Futures'),
      items: [{ name: t('USDT Perpetuals'), route: '/trade/2' }],
    },
    {
      label: t('More'),
      items: [
        { name: t('About Us'), route: '/about-us' },
        { name: t('Institutional'), route: '/institutional' },
        { name: t('Terms and Conditions'), route: '/terms-and-conditions' },
      ],
    },
    {
      label: t('Event Rewards'),
      items: [
        { name: t('Event Rewards'), route: '/reward' },
        { name: t('referralProgram'), route: '/affiliate-program' },
      ],
    },
    {
      label: t('Customer Service'),
      items: [
        { name: t('Support'), route: '/support' },
        { name: t('MMR'), route: '/leverage-margin' },
      ],
    },
  ];

  // 모바일용 단순 링크 리스트
  const mobileLinks = [
    { name: t('Buy/Sell Crypto'), route: '/buysell-crypto' },
    { name: t('Markets'), route: '/markets' },
    { name: t('Futures'), route: '/trade/2' },
    { name: t('About Us'), route: '/about-us' },
    { name: t('Institutional'), route: '/institutional' },
    { name: t('Events'), route: '/reward' },
    { name: t('Support'), route: '/support' },
    { name: t('MMR'), route: '/leverage-margin' },
  ];

  return (
    <Container>
      <FooterWrapper>
        {/* 데스크톱 레이아웃 */}
        <TopSection>
          <LogoSection>
            <LogoImage
              src="/assets/newGNB/logo-sunrise.svg"
              alt="Piecrip Logo"
            />
            <LogoTextImage
              src="/assets/newGNB/logo-piecrip.png"
              alt="Piecrip"
            />
          </LogoSection>

          <SettingsSection>
            <SelectBox>
              <Label>{t('Language')}</Label>
              <CSelect
                value={i18n.language}
                options={languageSelectOptions}
                onChange={handleLanguageChange}
              />
            </SelectBox>

            <SelectBox>
              <Label>{t('Currency')}</Label>
              <CSelect
                value={selectedCurrency?.code || 'USD'}
                options={currencySelectOptions}
                onChange={handleCurrencyChange}
              />
            </SelectBox>
          </SettingsSection>
        </TopSection>

        <MiddleSection>
          <LinksSection>
            {footerLinks.map((linkGroup, groupIndex) => (
              <LinkColumn key={groupIndex}>
                <LinkTitle>{linkGroup.label}</LinkTitle>
                {linkGroup.items.map((item, itemIndex) => (
                  <LinkItem
                    key={itemIndex}
                    onClick={() => handleNavigate(item.route)}
                  >
                    {item.name}
                  </LinkItem>
                ))}
              </LinkColumn>
            ))}
          </LinksSection>

          <QRSection>
            <QRTitle>{t('Download the App')}</QRTitle>
            <QRCodeWrapper>
              <QRCode
                value="https://storage.piecrip.com/download/piecrip.apk"
                size={140}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </QRCodeWrapper>
            <QRLogo src="/assets/newGNB/logo-piecrip.png" alt="Piecrip" />
          </QRSection>
        </MiddleSection>

        {/* 모바일 레이아웃 */}
        <MobileTopSection>
          <LogoSection>
            <LogoImage
              src="/assets/newGNB/logo-sunrise.svg"
              alt="Piecrip Logo"
            />
            <LogoTextImage
              src="/assets/newGNB/logo-piecrip.png"
              alt="Piecrip"
            />
          </LogoSection>

          {/* 모바일 전용 링크 섹션 */}
          <MobileLinksSection>
            {mobileLinks.map((link, index) => (
              <MobileLinkItem key={index} onClick={() => handleNavigate(link.route)}>
                {link.name}
              </MobileLinkItem>
            ))}
          </MobileLinksSection>

          <MobileSettingsSection>
            <SelectBox>
              <Label>{t('Language')}</Label>
              <CSelect
                value={i18n.language}
                options={languageSelectOptions}
                onChange={handleLanguageChange}
              />
            </SelectBox>

            <SelectBox>
              <Label>{t('Currency')}</Label>
              <CSelect
                value={selectedCurrency?.code || 'USD'}
                options={currencySelectOptions}
                onChange={handleCurrencyChange}
              />
            </SelectBox>
          </MobileSettingsSection>
        </MobileTopSection>

        <Divider />

        <BottomSection>
          <Copyright>Piecrip Copyright © 2025.</Copyright>
        </BottomSection>
      </FooterWrapper>
    </Container>
  );
};

export default React.memo(Footer);

const Container = styled.footer`
  width: 100%;
  background: linear-gradient(180deg, #051613 0%, #112824 100%);
  padding: 58px 0 40px 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1480px) {
    padding: 58px 16px 40px 16px;
  }

  @media (max-width: 768px) {
    padding: 46px 0 20px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  }
`;

const FooterWrapper = styled.div`
  width: 100%;
  max-width: 1440px;

  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 0 20px;
    max-width: 100%;
    margin: 0;
    position: relative;
  }
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 71px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileTopSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 39px;
    margin-bottom: 0;
    width: 100%;
    padding: 0;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const LogoImage = styled.img`
  height: 51px;
  width: auto;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 35px;
  }
`;

const LogoTextImage = styled.img`
  height: 34px;
  width: auto;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 23.341px;
  }
`;

const SettingsSection = styled.div`
  display: flex;
  gap: 39px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileSettingsSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    margin-top: 0;
    align-items: flex-start;
  }
`;

const SelectBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 165px;

  @media (max-width: 768px) {
    width: 165px;
    align-items: flex-start;
  }
`;

const Label = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  line-height: normal;

  @media (max-width: 768px) {
    font-size: 12px;
    letter-spacing: -0.24px;
  }
`;

const MiddleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 72px;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 60px;
    margin-bottom: 50px;
    align-items: flex-start;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const LinksSection = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const MobileLinksSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 12px;
    width: 100%;
  }
`;

const MobileLinkItem = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
  line-height: normal;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #ffffff;
  }
`;

const LinkColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 768px) {
    min-width: calc(50% - 15px);
  }
`;

const LinkTitle = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #ffffff;
  letter-spacing: -0.28px;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
`;

const LinkItem = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  line-height: normal;
  cursor: pointer;
  transition: color 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: #ffffff;
  }
`;

const QRSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 140px;

  @media (max-width: 1024px) {
    width: auto;
    align-self: flex-start;
  }
`;

const QRTitle = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #ffffff;
  letter-spacing: -0.28px;
  text-align: center;
  white-space: nowrap;
  line-height: normal;
`;

const QRCodeWrapper = styled.div`
  background: #ffffff;
  padding: 0;
  border-radius: 3.925px;
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const QRLogo = styled.img`
  width: 80px;
  height: auto;
  object-fit: contain;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #353535;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 100%;
    margin: 20px 0;
    position: relative;
    left: 0;
    transform: none;
  }
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Copyright = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  text-align: center;
  line-height: normal;

  @media (max-width: 768px) {
    font-size: 12px;
    letter-spacing: -0.24px;
  }
`;
